import { useMemo } from 'react'
import { useReadContract, useReadContracts } from 'wagmi'
import { ADDRESSES, IS_LIVE, ZERO_ADDR } from '@/lib/contracts'
import farmAbi from '@/lib/abis/HorizonEmissions.json'
import { POLL_SLOW, toNumber } from '@/lib/utils'

const BPS = 10_000
const SECONDS_PER_DAY = 86_400

const NOT_DEPLOYED = !IS_LIVE || (ADDRESSES.HORIZON_EMISSIONS || '').toLowerCase() === ZERO_ADDR

/// The HORIZON emission RATE on ALM vault shares, per vault - the "what would I earn" figure, as
/// opposed to `useVaultEmissions`'s "what have I earned" balance.
///
/// Reads the whole vault-farm table rather than looking up one vault at a time: `getVaultFarm`
/// returns the struct with its own `vault` field, so enumerating `vaultFarmCount` entries gives an
/// address->weight map in two staged calls regardless of how many vaults the page is rendering.
/// That beats a per-vault `vaultFarmIndex1` lookup as soon as the list has more than two rows, and
/// the table is genesis-curated and small by construction.
///
/// Rate = `emissionsPerSecond × weightBps / BPS`, the same arithmetic `_accrueVaultFarm` performs
/// on-chain. Exact for VAULT farms specifically: the APR governor (entry 180) deliberately exempts
/// them until a vault-share oracle exists, so no cap can bend this figure the way it bends the
/// reserve farms' (which read `effectiveRatePerSecond` in useProtocolState instead).
/// A registered vault sitting at zero weight therefore reads a real 0, not a blank -
/// that IS its rate, and it's the normal state for a freshly created vault, since
/// `createV3Vault.cjs` deliberately leaves the emission split to a genesis call. Vaults absent
/// from the table return `null` instead, so the UI can distinguish "earns nothing" from "does not
/// participate" rather than flattening both to zero.
export function useVaultEmissionRates() {
  const { data: emissionsPerSecond } = useReadContract({
    address: ADDRESSES.HORIZON_EMISSIONS, abi: farmAbi, functionName: 'emissionsPerSecond',
    query: { enabled: !NOT_DEPLOYED, refetchInterval: POLL_SLOW },
  })

  const { data: count } = useReadContract({
    address: ADDRESSES.HORIZON_EMISSIONS, abi: farmAbi, functionName: 'vaultFarmCount',
    query: { enabled: !NOT_DEPLOYED, refetchInterval: POLL_SLOW },
  })
  const n = Number(count ?? 0n)

  const { data: farmReads } = useReadContracts({
    query: { enabled: !NOT_DEPLOYED && n > 0, refetchInterval: POLL_SLOW },
    contracts: Array.from({ length: n }, (_, i) => ({
      address: ADDRESSES.HORIZON_EMISSIONS, abi: farmAbi, functionName: 'getVaultFarm', args: [BigInt(i)],
    })),
  })

  return useMemo(() => {
    const eps = emissionsPerSecond ?? 0n
    const rates = {}
    let total = 0

    for (let i = 0; i < n; i++) {
      const vf = farmReads?.[i]?.result
      if (!vf?.vault) continue
      // Integer-scale before converting: `eps × weightBps` stays exact in bigint, and only the
      // final per-day figure crosses into float for display.
      const perDay = toNumber((eps * (vf.weightBps ?? 0n) * BigInt(SECONDS_PER_DAY)) / BigInt(BPS))
      rates[vf.vault.toLowerCase()] = perDay
      total += perDay
    }

    return {
      horizonPerDayFor: (address) => (address ? rates[address.toLowerCase()] ?? null : null),
      vaultHorizonPerDay: total,
      notDeployed: NOT_DEPLOYED,
    }
  }, [emissionsPerSecond, farmReads, n])
}
